import {RegimeDriftBanner} from '@/components/regime-drift-banner'
import {SinSstAccion} from '@/components/sin-sst-accion'
import {ArlBanner} from '@/components/sgsst/arl-banner'
import {CertBlockedBanner} from '@/components/sgsst/cert-blocked-banner'
import {WorkerDivergenceBanner} from '@/components/sgsst/worker-divergence-banner'
import type {EntradaAvisoDePagina, SeveridadAviso} from '@/components/page-notices'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import type {SgsstItem, SgsstState} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {RESPONSABLE_STANDARD} from '@/hooks/sgsst/evaluacion/use-designations'

/** Función y no hook: la página la llama después de su `return` de guarda. Ningún aviso es efímero:
 * la norma exige verlos, así que se ordenan por severidad y `PageNotices` pliega los de abajo. */
export function avisosDeLaEvaluacion({
  businessId,
  state,
  driftSeverity,
  fase2,
  editable,
  isOwner,
  godReadOnly,
  isSystemUser,
  tieneSstVerificado,
  tieneResponsableDesignado,
  canDisenar,
  disenoBlockedReason,
  filaHospeda,
}: {
  businessId: string
  state: SgsstState
  driftSeverity: SeveridadAviso | null
  fase2: boolean
  editable: boolean
  isOwner: boolean
  godReadOnly: boolean
  isSystemUser: boolean
  tieneSstVerificado: boolean
  tieneResponsableDesignado: boolean
  canDisenar: boolean
  disenoBlockedReason: string | null
  /** De `filaHospedaElSinSst`; no equivale a «hay evaluación». */
  filaHospeda: boolean
}): EntradaAvisoDePagina[] {
  const assessment = state.assessment
  const snapshotBand = assessment?.snapshot_band
  const arlPendiente = !!assessment?.autoevaluacion_completed_at && !!snapshotBand && snapshotBand !== 'aceptable'
  return [
    // U3: con la fila 1.1.1 dibujada el aviso vive en ella; sin ella, esta pila es la única superficie.
    !tieneSstVerificado &&
      !filaHospeda && {
        id: 'sin-sst',
        severidad: 'amber' as const,
        nodo: (
          <SinSstAccion
            businessId={businessId}
            canAssign={isOwner && !godReadOnly}
            designado={tieneResponsableDesignado}
            // Sin atajo a propósito: designa contra la evaluación abierta (sin ella el POST da 404),
            // y esta pila se dibuja sobre todo cuando no la hay.
            roster={[]}
            // Sin la categoría que exige la banda, el atajo ofrecería a quien el gate rechaza.
            diseno={state.diseno_licencia}
          />
        ),
      },
    assessment &&
      !assessment.is_closed &&
      driftSeverity !== null && {
        id: 'drift',
        severidad: driftSeverity,
        nodo: <RegimeDriftBanner businessId={businessId} frozen={fase2} />,
      },
    // Puerta de diseño (confirmar sugeridos, certificar artefactos). God mode no lo ve: tiene sus
    // propias señales y ninguna licencia que renovar.
    assessment &&
      !assessment.is_closed &&
      !canDisenar &&
      !!disenoBlockedReason &&
      !isSystemUser && {
        id: 'cert-blocked',
        severidad: 'amber' as const,
        nodo: <CertBlockedBanner reason={disenoBlockedReason} businessId={businessId} />,
      },
    assessment &&
      !fase2 &&
      assessment.worker_count !== state.active_employee_count && {
        id: 'worker-divergence',
        severidad: 'amber' as const,
        nodo: <WorkerDivergenceBanner businessId={businessId} state={state} editable={editable} />,
      },
    // Un plan a la ARL vencido incumple el art. 28: va en rojo y encabeza la pila.
    assessment &&
      arlPendiente && {
        id: 'arl',
        severidad: (snapshotBand === 'critico' || assessment.arl_deadline_estado === 'vencido'
          ? 'red'
          : 'amber') as SeveridadAviso,
        nodo: <ArlBanner businessId={businessId} assessment={assessment} editable={editable} />,
      },
  ]
}

/** «Hay evaluación» no alcanza: la pestaña «Artefactos» y los filtros pueden ocultar 1.1.1, así que
 * se pregunta por la pestaña activa y los grupos ya filtrados. */
export function filaHospedaElSinSst(tab: string, groups: {items: SgsstItem[]}[]): boolean {
  return tab === 'evaluacion' && groups.some((g) => g.items.some((i) => i.code === RESPONSABLE_STANDARD))
}

/** U3: el aviso vive en la fila 1.1.1. Con `esperaDesignacion` la fila ya lleva su chip y el aviso no
 * repite el estado. */
export function avisoSinSstDeLaFila({
  item,
  businessId,
  state,
  puedeAsignar,
  tieneSstVerificado,
  tieneResponsableDesignado,
  esperaDesignacion,
  roster,
}: {
  item: SgsstItem
  businessId: string
  state: SgsstState
  puedeAsignar: boolean
  tieneSstVerificado: boolean
  tieneResponsableDesignado: boolean
  esperaDesignacion: boolean
  roster: Employee[]
}) {
  if (item.code !== RESPONSABLE_STANDARD || tieneSstVerificado) return null
  const assessment = state.assessment
  return (
    <SinSstAccion
      businessId={businessId}
      canAssign={puedeAsignar}
      designado={tieneResponsableDesignado}
      // El atajo designa contra la evaluación abierta; sin ella el POST responde 404.
      roster={assessment && !assessment.is_closed ? roster : []}
      diseno={state.diseno_licencia}
      conEstado={!esperaDesignacion}
    />
  )
}
